import styled from '@emotion/styled';
import React, { FC } from 'react';
import {
    Slide,
    toast,
    ToastContainer as ToastifyToastContainer,
    ToastContent,
    ToastOptions,
} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Icon, Icons } from 'src/shared/ds/components/Icon';
import { ComponentTextSmall } from 'src/shared/ds/Typography';
import { pxToRem } from 'src/shared/helpers/pxToRem';
import { dsColors } from '../variables';

export type MessageType = 'warning' | 'error' | 'success' | 'info';

export const ToastContainer = styled(ToastifyToastContainer)({
    '&.Toastify__toast-container--top-center': {
        top: 0,
        padding: 0,
        width: '100%',
        maxWidth: 1440,
    },
    '.Toastify__toast': {
        marginBottom: 0,
        minHeight: 0,
        padding: 14,
        borderRadius: 0,
    },
    '.Toastify__toast-body': {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 0,
    },

    '.Toastify__toast--success': {
        backgroundColor: dsColors.cyan300,
    },
    '.Toastify__toast--warning': {
        backgroundColor: dsColors.orange600,
    },
    '.Toastify__toast--error': {
        backgroundColor: dsColors.red400,
    },
    '.Toastify__toast--info': {
        backgroundColor: dsColors.blue500,
    },

    '.notification-icon': {
        marginRight: pxToRem(11),
    },
});

interface NotificationMessageProps {
    title: string;
    icon: Icons;
}

const NotificationMessage: FC<NotificationMessageProps> = ({ title, icon }) => {
    return (
        <>
            <Icon name={icon} color="white" className="notification-icon" />
            <ComponentTextSmall fontColor="white">{title}</ComponentTextSmall>
        </>
    );
};

const mapMessageTypeToIcon: Record<MessageType, Icons> = {
    warning: 'warning-enclosed-filled',
    error: 'x-enclosed-filled',
    success: 'checkmark-enclosed-filled',
    info: 'info-enclosed-filled',
};

const renderMessageFunction: Record<
    MessageType,
    (content: ToastContent, options?: ToastOptions | undefined) => React.ReactText
> = {
    warning: toast.warning,
    error: toast.error,
    success: toast.success,
    info: toast.info,
};

interface RenderMessageArguments {
    content: string;
    type: MessageType;
    duration: number;
}

export const renderMessage = ({ content, type, duration }: RenderMessageArguments) => {
    renderMessageFunction[type](
        () => <NotificationMessage title={content} icon={mapMessageTypeToIcon[type]} />,
        {
            position: 'top-center',
            autoClose: duration * 1000, // autoClose time is in ms and duration in sec
            draggable: false,
            hideProgressBar: true,
            closeButton: false,
            transition: Slide,
        },
    );
};
